import { useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapPin, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface SimpleLocationMapProps {
  latitude: number;
  longitude: number;
  name?: string;
  className?: string;
}

/**
 * A lightweight map component for displaying a single location marker.
 * Uses a callback ref to ensure the container is mounted before initializing Mapbox.
 */
const SimpleLocationMap = ({ latitude, longitude, name, className = "" }: SimpleLocationMapProps) => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);

  // Load Mapbox token
  useEffect(() => {
    const loadToken = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke('get-mapbox-token');
        if (fnError) throw fnError;
        if (data?.token) {
          setMapboxToken(data.token);
        } else {
          setError('Token de mapa no configurado');
        }
      } catch (e) {
        console.error('Failed to load Mapbox token:', e);
        setError('Error cargando el mapa');
      }
    };
    loadToken();
  }, []);

  // Callback ref - initializes the map when the DOM element is available
  const initMap = useCallback((node: HTMLDivElement | null) => {
    if (!node || !mapboxToken) return;
    
    // Prevent re-initialization
    if (mapInstance) return;

    // Check WebGL support
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      setError('Tu navegador no soporta mapas interactivos');
      return;
    }

    try {
      mapboxgl.accessToken = mapboxToken;

      const map = new mapboxgl.Map({
        container: node,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [longitude, latitude],
        zoom: 14,
        attributionControl: false,
      });

      map.addControl(
        new mapboxgl.AttributionControl({ compact: true }),
        'bottom-right'
      );

      map.addControl(
        new mapboxgl.NavigationControl({ visualizePitch: false }),
        "top-right"
      );

      // Add marker on load
      map.on('load', () => {
        new mapboxgl.Marker({ color: '#FF4444' })
          .setLngLat([longitude, latitude])
          .setPopup(
            name 
              ? new mapboxgl.Popup({ offset: 25 }).setHTML(
                  `<div style="padding: 8px;"><strong>${name}</strong></div>`
                )
              : undefined
          )
          .addTo(map);

        // Force resize to handle lazy-loaded containers
        map.resize();
      });

      map.on('error', (e) => {
        console.warn('Mapbox error:', e);
      });

      setMapInstance(map);
    } catch (e) {
      console.error('Failed to initialize map:', e);
      setError('Error inicializando el mapa');
    }
  }, [mapboxToken, latitude, longitude, name, mapInstance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [mapInstance]);

  // Re-trigger init when token loads (callback ref alone won't re-run)
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);

  useEffect(() => {
    if (containerNode && mapboxToken && !mapInstance) {
      initMap(containerNode);
    }
  }, [containerNode, mapboxToken, mapInstance, initMap]);

  if (error) {
    return (
      <div className={`w-full h-[400px] bg-muted rounded-xl flex flex-col items-center justify-center gap-3 ${className}`}>
        <AlertCircle className="w-10 h-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!mapboxToken) {
    return (
      <div className={`w-full h-[400px] bg-muted rounded-xl flex flex-col items-center justify-center gap-3 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Cargando mapa...</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={`w-full h-[400px] rounded-xl overflow-hidden ${className}`}
    />
  );
};

export default SimpleLocationMap;
